import { httpsCallable } from 'firebase/functions';
import { functions, usingEmulators } from '../firebase';
import { isFullCode, isShortCode } from './pins';

/**
 * Google lookups (Places, Geocoding, Routes, drive-time matrices) go only through Routr's server: the `maps` Cloud
 * Function (functions/src/maps.ts) checks each one, counts it against the account's day and asks Google with a key
 * that never reaches the browser. This is the one boundary: on the emulators (and in tests) it answers from a fixed
 * list of Abuja places instead, as the phone's ROUTR_PLACES_MOCK does, and Routes and matrices are unavailable, so
 * callers fall back to straight lines as the phone does.
 */

export type Op = 'autocomplete' | 'details' | 'geocode' | 'routes' | 'matrix';

export class LookupError extends Error {
  constructor(
    readonly why: 'limit' | 'unavailable' | 'signIn' | 'bad',
    message: string,
  ) {
    super(message);
  }
}

/** Answers on the emulators: VITE_MAPS_LIVE=true asks the functions emulator's `maps` instead. */
export const stubbed = usingEmulators && import.meta.env.VITE_MAPS_LIVE !== 'true';

/** The places the stub knows: the design page's sample places, around AgegeBurger in Abuja. */
export const STUB_PLACES: { id: string; main: string; area: string; lat: number; lng: number }[] = [
  ['7 Yedseram Street', 'Maitama', 9.0925, 7.4995],
  ['22 Nile Street', 'Maitama', 9.096, 7.493],
  ['3 Agadez Crescent', 'Wuse 2', 9.0745, 7.484],
  ['11 Lobito Crescent', 'Wuse 2', 9.0812, 7.475],
  ['5 Durban Street', 'Wuse 2', 9.0778, 7.4805],
  ['17 Mambilla Street', 'Asokoro', 9.044, 7.521],
  ['2 Kainji Crescent', 'Maitama', 9.089, 7.487],
  ['9 Volta Street', 'Maitama', 9.0915, 7.4965],
  ['14 Gana Street', 'Maitama', 9.0872, 7.4905],
  ['6 Ogbomosho Street', 'Area 8', 9.042, 7.487],
  ['28 Herbert Macaulay Way', 'Wuse', 9.06, 7.476],
  ['4 Usuma Street', 'Maitama', 9.0948, 7.5072],
].map(([main, area, lat, lng], i) => ({ id: `stub-place-${i}`, main: String(main), area: String(area), lat: Number(lat), lng: Number(lng) }));

function stub(op: Op, params: Record<string, unknown>): Record<string, unknown> {
  if (op === 'autocomplete') {
    // A written address ("14 Gana Street, Maitama, Abuja") is matched by its first part, as Google would find it.
    const q = String(params.input ?? '').split(',')[0].trim().toLowerCase();
    const hits = STUB_PLACES.filter((p) => `${p.main} ${p.area}`.toLowerCase().includes(q) || p.main.toLowerCase().replace(/^\d+\s+/, '').startsWith(q));
    return {
      status: hits.length ? 'OK' : 'ZERO_RESULTS',
      predictions: hits.slice(0, 5).map((p) => ({ place_id: p.id, description: `${p.main}, ${p.area}, Abuja, Nigeria`, structured_formatting: { main_text: p.main, secondary_text: `${p.area}, Abuja, Nigeria` } })),
    };
  }
  if (op === 'details' || op === 'geocode') {
    const p = STUB_PLACES.find((x) => x.id === params.place_id);
    if (!p) return { status: 'ZERO_RESULTS', ...(op === 'geocode' ? { results: [] } : {}) };
    const house = /^(\d+)\s/.exec(p.main)?.[1];
    const result = {
      place_id: p.id,
      formatted_address: `${p.main}, ${p.area}, Abuja, Nigeria`,
      geometry: { location: { lat: p.lat, lng: p.lng }, location_type: 'ROOFTOP' },
      address_components: [...(house ? [{ long_name: house, short_name: house, types: ['street_number'] }] : []), { long_name: p.area, short_name: p.area, types: ['sublocality'] }],
    };
    return op === 'details' ? { status: 'OK', result } : { status: 'OK', results: [result] };
  }
  throw new LookupError('unavailable', 'Routes and drive times aren’t asked on the emulators');
}

const REFUSED = new Set(['REQUEST_DENIED', 'OVER_QUERY_LIMIT', 'OVER_DAILY_LIMIT', 'UNKNOWN_ERROR']);

/** One lookup through Routr's server; [LookupError] says why it couldn't be made. */
export async function lookup(op: Op, params: Record<string, unknown>): Promise<Record<string, unknown>> {
  if (stubbed) return stub(op, params);
  try {
    const r = await httpsCallable<{ op: Op; params: Record<string, unknown> }, Record<string, unknown>>(functions, 'maps')({ op, params });
    // Places and Geocoding answer 200 with their own status: a refusal is no reason to say an address wasn't found.
    if (REFUSED.has(String(r.data.status))) throw new LookupError('unavailable', 'Google didn’t answer that lookup. Try again soon, or paste the customer’s pin.');
    return r.data;
  } catch (e) {
    if (e instanceof LookupError) throw e;
    const code = (e as { code?: string }).code ?? '';
    if (code.endsWith('resource-exhausted')) throw new LookupError('limit', 'Today’s lookups for this account are used up; they come back tomorrow.');
    if (code.endsWith('unauthenticated')) throw new LookupError('signIn', 'Sign in again to look addresses up.');
    if (code.endsWith('invalid-argument')) throw new LookupError('bad', 'Routr’s server couldn’t read that request.');
    throw new LookupError('unavailable', 'Routr’s server didn’t answer. Check the connection and try again.');
  }
}

/** A search session (Places bills a typed search and its pick as one). */
export const newSession = () => crypto.randomUUID();

/** A Google result as a stop (PlacesService.getDetails / parseGeocode). */
export interface GooglePlace {
  address: string;
  lat: number;
  lng: number;
  placeId?: string;
  houseNumber?: string;
  /** Exact only with a house number Google placed on the building or along the street, a full match and no plus code. */
  precision: 'exact' | 'low';
  /** Geocoding found only the area (a district, a town): not good enough for a stop, so the address search goes on. */
  areaOnly: boolean;
}

export function googlePlace(r: Record<string, unknown>, geocode: boolean): GooglePlace | null {
  const geometry = r.geometry && typeof r.geometry === 'object' ? (r.geometry as Record<string, unknown>) : null;
  const loc = geometry && geometry.location && typeof geometry.location === 'object' ? (geometry.location as Record<string, unknown>) : null;
  if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return null;
  const address = String(r.formatted_address ?? r.name ?? '');
  let houseNumber: string | undefined;
  for (const c of Array.isArray(r.address_components) ? r.address_components : []) {
    if (c && Array.isArray(c.types) && c.types.includes('street_number')) {
      houseNumber = String(c.long_name ?? '') || undefined;
      break;
    }
  }
  const word = address.split(',')[0].trim().split(' ')[0];
  const plusCode = isFullCode(word) || isShortCode(word);
  const type = String(geometry!.location_type ?? 'APPROXIMATE');
  const placed = !geocode || (r.partial_match !== true && (type === 'ROOFTOP' || type === 'RANGE_INTERPOLATED'));
  return {
    address,
    lat: loc.lat,
    lng: loc.lng,
    placeId: typeof r.place_id === 'string' ? r.place_id : undefined,
    houseNumber,
    precision: houseNumber && !plusCode && placed ? 'exact' : 'low',
    areaOnly: geocode && type === 'APPROXIMATE',
  };
}

